import { type WalletOperation } from './wallet-operation';

export interface WalletSendSuggestionSnapshot {
  open: boolean;
  checking: boolean;
}

interface SuggestionDependencies {
  beginOperation: () => WalletOperation;
  takeReminder: () => Promise<boolean>;
}

interface PendingSend {
  operation: WalletOperation;
  send: () => void;
}

const CLOSED: WalletSendSuggestionSnapshot = { open: false, checking: false };
const CHECKING: WalletSendSuggestionSnapshot = { open: false, checking: true };
const OPEN: WalletSendSuggestionSnapshot = { open: true, checking: false };

export class WalletSendSuggestionController {
  private pending: PendingSend | null = null;
  private version = 0;
  private snapshot = CLOSED;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly deps: SuggestionDependencies) {}

  getSnapshot = (): WalletSendSuggestionSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  request = async (send: () => void): Promise<void> => {
    if (this.snapshot.checking || this.snapshot.open) {
      return;
    }

    const version = this.version;
    const operation = this.deps.beginOperation();
    this.publish(CHECKING);
    const offer = operation.isCurrent() && (await this.deps.takeReminder());

    if (version !== this.version) {
      return;
    }

    if (offer && operation.isCurrent()) {
      this.pending = { operation, send };
      this.publish(OPEN);
      return;
    }

    this.publish(CLOSED);

    if (operation.isCurrent()) {
      send();
    }
  };

  continueSend = (): void => {
    this.takePending()?.send();
  };

  addAuthenticator = (): boolean => this.takePending() !== null;

  cancel = (): void => {
    this.version += 1;
    this.pending = null;
    this.publish(CLOSED);
  };

  private takePending(): PendingSend | null {
    const pending = this.pending;
    this.pending = null;
    this.publish(CLOSED);
    return pending?.operation.isCurrent() ? pending : null;
  }

  private publish(snapshot: WalletSendSuggestionSnapshot): void {
    this.snapshot = snapshot;

    for (const listener of this.listeners) {
      listener();
    }
  }
}
